import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import CountUp from "react-countup";
import { Link } from "react-router-dom";
import {
  FiEye,
} from "react-icons/fi";
import { useAboutContext } from "../../context/About_context";
import Loader from "../../Common/loader/Loader";
import { renderTitleWithGradient } from "../../utils/helper";
import "./WhyVisit.css";

/* Motion Reveal Component */
const Reveal = ({ children, className = "", delay = 0 }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.05 }}
      transition={{
        duration: 0.7,
        delay: delay / 1000,
        ease: [0.16, 1, 0.3, 1],
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

/* Animated Number Counter (React CountUp) */
const AnimatedNumber = ({ value }) => {
  const numericPart =
    parseInt(
      String(value)
        .replace(/,/g, "")
        .replace(/[^0-9]/g, ""),
      10,
    ) || 0;
  const hasPlus = String(value).includes("+");
  const hasComma = String(value).includes(",");

  return (
    <CountUp
      start={0}
      end={numericPart}
      duration={2}
      separator={hasComma ? "," : ""}
      suffix={hasPlus ? "+" : ""}
      enableScrollSpy
      scrollSpyOnce
    />
  );
};

const WhyVisit = () => {
  const { whyvisit_loading, whyvisit_data, GetWhyVisit } = useAboutContext();

  useEffect(() => {
    GetWhyVisit();
  }, []); // eslint-disable-line

  if (
    whyvisit_loading &&
    (!whyvisit_data || Object.keys(whyvisit_data).length === 0)
  ) {
    return <Loader fullScreen={true} text="Loading Why Visit Data..." />;
  }

  return (
    <div className="why-visit-page">
      <Helmet>
        <title>Why Visit | Bharat Buildcon</title>
      </Helmet>
      {/* Light Header Section */}
      <div className="wv-top-section">
        <div className="wv-container">
          {/* Breadcrumb Pill */}
          <div className="wv-breadcrumb-wrapper">
            <nav className="wv-breadcrumb" aria-label="Breadcrumb">
              <Link to="/" className="wv-bc-home">
                HOME
              </Link>
              <span className="wv-bc-slash">/</span>
              <span className="wv-bc-active">WHY VISIT</span>
            </nav>
          </div>

          {/* Page Main Heading */}
          {whyvisit_data?.why_exhibit && (
            <Reveal delay={50}>
              <h1 className="wv-main-title">
                {renderTitleWithGradient(whyvisit_data.why_exhibit)}
              </h1>
            </Reveal>
          )}

          {/* Subtitle / Description */}
          {whyvisit_data?.wv_desc && (
            <Reveal delay={100}>
              <p className="wv-subtitle">{whyvisit_data.wv_desc}</p>
            </Reveal>
          )}

          {/* Connected Stat Cards */}
          {Array.isArray(whyvisit_data?.whyvisit_Count) &&
            whyvisit_data.whyvisit_Count.length > 0 && (
              <Reveal delay={150}>
                <div className="wv-stats-container">
                  <div
                    className={`wv-stats-grid cols-${Math.min(
                      whyvisit_data.whyvisit_Count.length,
                      4,
                    )}`}
                  >
                    {whyvisit_data.whyvisit_Count.map((stat, idx) => {
                      const totalCount = whyvisit_data.whyvisit_Count.length;
                      const isLast = idx === totalCount - 1;
                      const colCount = Math.min(totalCount, 4);
                      const isRowEndCol4 = (idx + 1) % colCount === 0;
                      const isEvenCard = (idx + 1) % 2 === 0;

                      return (
                        <React.Fragment key={idx}>
                          <div className="wv-stat-card">
                            <div className="wv-stat-val">
                              <AnimatedNumber value={stat.count || "0"} />
                            </div>
                            <div className="wv-stat-lbl">{stat.name}</div>
                          </div>

                          {!isLast && (
                            <>
                              {/* Horizontal Connector for Desktop / Tablet */}
                              {!isRowEndCol4 && (
                                <div
                                  className={`wv-stat-connector ${
                                    isEvenCard ? "h-conn-even" : "h-conn-odd"
                                  }`}
                                >
                                  <span className="wv-stat-line" />
                                  <span className="wv-stat-dot" />
                                  <span className="wv-stat-line" />
                                </div>
                              )}

                              {/* Vertical Connector for Mobile */}
                              <div className="wv-stat-connector-vertical">
                                <span className="wv-stat-line-v" />
                                <span className="wv-stat-dot-v" />
                                <span className="wv-stat-line-v" />
                              </div>
                            </>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              </Reveal>
            )}
        </div>
      </div>

      {/* Main Dark Section */}
      <div className="wv-dark-section">
        <div className="wv-container">
          {whyvisit_data?.sponsership_rate_banifit && (
            <Reveal delay={200}>
              <h2 className="wv-dark-heading">
                {renderTitleWithGradient(
                  whyvisit_data.sponsership_rate_banifit,
                )}
              </h2>
            </Reveal>
          )}

          {/* Feature Cards Grid */}
          {Array.isArray(whyvisit_data?.whyvisit_childs) && (
            <div className="wv-features-grid">
              {whyvisit_data.whyvisit_childs.map((card, idx) => {
                const titleStr = card.title?.trim() || "";
                const cleanTitle = titleStr.endsWith(":")
                  ? titleStr.slice(0, -1)
                  : titleStr;

                return (
                  <Reveal key={card.id || idx} delay={250 + idx * 50}>
                    <div className="wv-feature-card">
                      <div className="wv-card-icon-box">
                        <FiEye />
                      </div>
                      <h3 className="wv-card-title">{cleanTitle}</h3>
                      <p className="wv-card-desc">{card.wv_remark}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WhyVisit;
